import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { of } from 'rxjs';
import * as _ from 'lodash';

import { isNumeric } from '@/app/core/utils/math';
import { RdoHttpService } from '@/app/core/http/rdo-http.service';
import {
  ClientCategory,
  ClientMonth,
  CustomerSize,
  GeoFilterItem,
  GeographyQuery,
  FilterLabel,
  OutlierReason,
  RouseCategory,
  SearchQuery,
  LocaleFormat,
  ClientVertical,
  CatProductGroup,
  ClientProductType,
  RouseProductType,
  RouseMarket,
  RouseProductTypeQuery,
  ClientProductTypeQuery,
} from '@/app/models';
import { AuthenticationService } from '@/app/core/authentication/authentication.service';
import { FormatService } from '@/app/core/query/format.service';
import { CODENAMES } from '@/app/core/constants';
import { FilterProfileService } from '@/app/filter/profiles/filter-profile.service';
import { ErrorService } from '@/app/core/errors/error.service';
import { AuthForbiddenErrorService } from '@/app/core/errors/auth-forbidden-error.service';
import { retryBeforeThrow } from '@/app/core/utils/retry-before-throw';

type SelectOption = {
  Value: any;
  DisplayName: string;
};

@Injectable({
  providedIn: 'root',
})
export class FilterDataService {
  public benchmarks: Array<any>;
  public geographyLevels: Array<any>;
  public regionsCache: Array<GeoFilterItem>;
  public districtsCache: Array<GeoFilterItem>;
  public branchesCache: Array<GeoFilterItem>;
  constructor(
    private authenticationService: AuthenticationService,
    private http: RdoHttpService,
    private formatService: FormatService,
    private filterProfileService: FilterProfileService,
    private errorService: ErrorService,
    private forbiddenService: AuthForbiddenErrorService
  ) {
    this.benchmarks = [];
    this.geographyLevels = [];
  }

  protected handleError(err: any, defaultMessage: string) {
    console.log('filter-data.service.handleError  ', err, defaultMessage);
    this.errorService.handleError(err);
    this.forbiddenService.handleError(err);

    if (err.error) {
      return throwError(err.error);
    } else if (err.message) {
      return throwError(err);
    }
    return throwError(() => new Error(defaultMessage));
  }

  getMonths(): Observable<Array<ClientMonth>> {
    return this.http
      .clone()
      .disableRoutingAbort()
      .get('/filter/months')
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getMonths' }),
        map((res) => {
          const dates = new Array<ClientMonth>();
          for (const month of res as any) {
            const date = new Date(1970, 0, 1);
            date.setMonth(parseInt(month.Month));

            dates.push(new ClientMonth(month.MonthID, date));
          }
          return dates;
        }),
        catchError((err) => {
          return this.handleError(err, 'FilterDataService.getMonths Error');
        })
      );
  }

  getFilterLabels = (): Observable<Array<FilterLabel>> => {
    const clientid =
      this.authenticationService._userInfoView.SelectedClient.ClientID;
    const url = `/filter/filter-labels/${clientid.toString()}`;
    return this.http
      .clone()
      .disableRoutingAbort()
      .get(url)
      .pipe(
        map((res) => res as Array<FilterLabel>),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getFilterLabels Error'
          );
        })
      );
  };

  getRegions(): Observable<Array<GeoFilterItem>> {
    // The query is being limited by the 3 exceptions below. Adding these keys as exceptions will allow the query to return all regions.
    const override = new SearchQuery({
      BranchList: [],
      RegionList: [],
      DistrictList: [],
    });
    const url = '/filter/regions';
    return this.http
      .clone()
      .disableRoutingAbort()
      .post(url, null, override)
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getRegions' }),
        map((result) =>
          (result as any).map(
            (x: any) => new GeoFilterItem(x.RegionId, x.Region)
          )
        ),
        catchError((err) => {
          return this.handleError(err, 'FilterDataService.getRegions Error');
        })
      )
      .pipe(tap((res) => (this.regionsCache = res)));
  }

  getDistricts(regions: Array<number>): Observable<Array<GeoFilterItem>> {
    const body = new GeographyQuery();
    const override = new SearchQuery({ DistrictList: [], BranchList: [] });
    // body.RegionList = regions;
    body.RegionList = [];
    const url = '/filter/districts';
    return this.http
      .clone()
      .disableRoutingAbort()
      .post(url, body, override)
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getDistricts' }),
        map((result) =>
          _.map(
            result as any,
            (x: any) => new GeoFilterItem(x.DistrictId, x.District, x.RegionId)
          )
        ),
        catchError((err) => {
          return this.handleError(err, 'FilterDataService.getDistricts Error');
        })
      )
      .pipe(tap((res) => (this.districtsCache = res)));
  }

  // https://rouseservices.atlassian.net/browse/RIE-5477
  // When we request branches with the current list of selected regions then only the branches for those regions are returned.
  // There is an issue then when the user switches branches within the filters dropdown. The branch list is now limited.
  // In this case pass an empty array for regions and districts to get all branches.
  getBranches(
    regions: Array<number>,
    districts: Array<number>
  ): Observable<Array<GeoFilterItem>> {
    const body = new GeographyQuery();
    const override = new SearchQuery({ BranchList: [] });
    body.RegionList = regions;
    body.DistrictList = districts;

    return this.http
      .clone()
      .disableRoutingAbort()
      .post('/filter/locationCodes', body, override)
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getBranches' }),
        map((result) =>
          _.map(
            result as any,
            (x: any) =>
              new GeoFilterItem(x.BranchId, x.Branch, x.DistrictId, x.RegionId)
          )
        ),
        catchError((err) => {
          return this.handleError(err, 'FilterDataService.getBranches Error');
        })
      )
      .pipe(tap((res) => (this.branchesCache = res)));
  }

  getEnglishBenchmarks(): Observable<any[]> {
    return this.http
      .get(`/filter/benchmarks/en-US`)
      .pipe(map(this.createBenchmarks));
  }

  getBenchmarks(lang: string = null): Observable<any[]> {
    if (this.benchmarks.length > 0) {
      return new Observable((obs) => {
        obs.next(this.benchmarks);
        obs.complete();
      });
    }
    let languageParam = '';
    if (lang) {
      languageParam = `/${lang}`;
    }
    return this.http
      .clone()
      .disableRoutingAbort()
      .get(`/filter/benchmarks${languageParam}`)
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getBenchmarks' }),
        catchError((err) => {
          return this.handleError(err, 'FilterDataService.getBenchmarks Error');
        }),
        map(this.createBenchmarks)
      );
  }

  private createBenchmarks = (resp: any): any[] => {
    this.benchmarks = resp;
    return this.benchmarks;
  };

  getComparisons(): Observable<any[]> {
    return of([
      {
        Cid: 1,
        DisplayName: 'main.filters.primary_comparison.benchmark_focused',
      },
      { Cid: 2, DisplayName: 'main.filters.primary_comparison.book_focused' },
    ]);
  }

  getPrimaryProductTypeOptions(): Observable<SelectOption[]> {
    return of([
      {
        Value: 0,
        DisplayName: 'main.filters.primary-product-type.equipment',
      },
      {
        Value: 1,
        DisplayName: 'main.filters.primary-product-type.rented-as',
      },
    ]);
  }

  getGeographyLevels(): Observable<any[]> {
    if (this.geographyLevels.length > 0) {
      return new Observable((obs) => {
        obs.next(this.geographyLevels);
        obs.complete();
      });
    }
    return this.http
      .clone()
      .disableRoutingAbort()
      .get('/filter/geography-levels')
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getGeographyLevels' }),
        map(this.createGeographyLevels),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getGeographyLevels Error'
          );
        })
      );
  }

  getCustomGridsGeographyLevels(target: string): Observable<any[]> {
    return this.http.get(`/filter/customgrids-geography-levels/${target}`).pipe(
      map(this.createGeographyLevels),
      catchError((err) => {
        return this.handleError(
          err,
          'FilterDataService.getCustomGridsGeographyLevels Error'
        );
      })
    );
  }

  getEnglishGeographyLevels(): Observable<any[]> {
    return this.http
      .get('/filter/geography-levels/en-US')
      .pipe(map(this.createGeographyLevels));
  }

  private createGeographyLevels = (resp: any): Array<any> => {
    this.geographyLevels = resp;
    return this.geographyLevels;
  };

  getRouseCategories(): Observable<Array<RouseCategory>> {
    return this.http
      .clone()
      .disableRoutingAbort()
      .get('/filter/rouse-categories')
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getRouseCategories' }),
        map((res) => res as Array<RouseCategory>),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getRouseCategories Error'
          );
        })
      );
  }

  getClientCategories(): Observable<Array<ClientCategory>> {
    const clientid = this.authenticationService._userInfoView.SelectedClient
      ? this.authenticationService._userInfoView.SelectedClient.ClientID
      : undefined;
    let endpoint = '/filter/client-categories';

    if (isNumeric(clientid) === true) {
      endpoint = endpoint + '?ClientId=' + clientid.toString();
    }

    return this.http
      .clone()
      .disableRoutingAbort()
      .get(endpoint)
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getClientCategories' }),
        map((res) => res as Array<ClientCategory>),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getClientCategories Error'
          );
        })
      );
  }

  getCatProductGroups(): Observable<Array<CatProductGroup>> {
    return this.http
      .clone()
      .disableRoutingAbort()
      .get('/filter/cat-product-groups') // '/filter/client-categories')
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getCatProductGroups' }),
        map((res) => res as Array<CatProductGroup>),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getCatProductGroups Error'
          );
        })
      );
  }

  getClientProductTypes(
    categories?: Array<number>
  ): Observable<Array<ClientProductType>> {
    const body = new ClientProductTypeQuery();
    body.ClientCategoryList = categories;
    let endpoint = '/filter/client-product-types';

    const clientid = this.authenticationService._userInfoView.SelectedClient
      ? this.authenticationService._userInfoView.SelectedClient.ClientID
      : undefined;

    if (isNumeric(clientid) === true) {
      endpoint = endpoint + '?ClientId=' + clientid.toString();
    }

    return this.http
      .clone()
      .disableRoutingAbort()
      .post(endpoint, null)
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getClientProductTypes' }),
        map((res) => res as Array<ClientProductType>),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getClientProductTypes Error'
          );
        })
      );
  }

  async getRouseProductTypes(categories?: Array<number>) {
    const body = new RouseProductTypeQuery();
    body.RouseCategoryList = categories;

    const useRentedAsProductType =
      await this.filterProfileService.readPropertyAsync(
        CODENAMES.CN_SHOW_RENTED_AS
      );

    body.UseRentedAsProductType = useRentedAsProductType === 1 ? true : false;
    let endpoint = '/filter/rouse-product-types';
    const clientid = this.authenticationService._userInfoView.SelectedClient
      ? this.authenticationService._userInfoView.SelectedClient.ClientID
      : undefined;
    if (isNumeric(clientid) === true) {
      endpoint = endpoint + '?ClientId=' + clientid.toString();
    }
    return this.http
      .clone()
      .disableRoutingAbort()
      .post(endpoint, body)
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getRouseProductTypes' }),
        map((res) => res as Array<RouseProductType>),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getRouseProductTypes Error'
          );
        })
      );
  }

  getRouseMarkets(): Observable<Array<RouseMarket>> {
    return this.http
      .clone()
      .disableRoutingAbort()
      .get('/filter/rouse-markets')
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getRouseMarkets' }),
        map((res) => res as Array<RouseMarket>),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getRouseMarkets Error'
          );
        })
      );
  }

  getCustomerSizes = (): Observable<Array<CustomerSize>> => {
    return this.http
      .clone()
      .disableRoutingAbort()
      .get('/filter/customer-sizes')
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getCustomerSizes' }),
        map((res) => res as Array<CustomerSize>),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getCustomerSizes Error'
          );
        })
      );
  };

  getClientOutlierReasons = (): Observable<Array<OutlierReason>> => {
    return this.http
      .clone()
      .disableRoutingAbort()
      .get('/filter/client-outlier-reasons')
      .pipe(
        retryBeforeThrow({
          label: 'FilterDataService.getClientOutlierReasons',
        }),
        map((res) => res as Array<OutlierReason>),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getClientOutlierReasons Error'
          );
        })
      );
  };

  getClientVerticals = (): Observable<Array<ClientVertical>> => {
    return this.http
      .clone()
      .disableRoutingAbort()
      .get('/filter/client-verticals')
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getClientVerticals' }),
        map((res) => res as Array<ClientVertical>),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getClientVerticals Error'
          );
        })
      );
  };

  getCycleBillTitle(
    min: number,
    max: number,
    xlsExport?: boolean
  ): string | string[] {
    let title = '';
    let titleRange = '';
    if (min === 1 && max === 1) {
      title = 'main.filters.include_initial_bill_only'; // 'Include Initial Bill Only';
    } else if (min === 2 && max === 2) {
      titleRange = '2-3'; // 'Include Cycle Bill 2-3 Only';
    } else if (min === 3 && max === 3) {
      titleRange = '4-6'; // 'Include Cycle Bill 4-6 Only';
    } else if (min === 4 && max === 4) {
      titleRange = '7-12'; // 'Include Cycle Bill 7-12 Only';
    } else if (min === 5 && max === 5) {
      titleRange = '> 12'; //  'Include Cycle Bill > 12 Only';
    } else if (min === 1 && max === 2) {
      titleRange = '1-3'; // 'Include Cycle Bill 1-3 Only';
    } else if (min === 2 && max === 3) {
      titleRange = '2-6'; // 'Include Cycle Bill 2-6 Only';
    } else if (min === 3 && max === 4) {
      titleRange = '4-12'; // 'Include Cycle Bill 4-12 Only';
    } else if (min === 4 && max === 5) {
      titleRange = '> 6'; // 'Include Cycle Bill > 6 Only';
      // eslint-disable-next-line no-dupe-else-if
    } else if (min === 1 && max === 2) {
      titleRange = '1-3'; // 'Include Cycle Bill 1-3 Only';
    } else if (min === 1 && max === 3) {
      titleRange = '1-6'; // 'Include Cycle Bill 1-6 Only';
    } else if (min === 1 && max === 4) {
      titleRange = '1-12'; // 'Include Cycle Bill 1-12 Only';
    } else if (min === 2 && max === 4) {
      titleRange = '2-12'; // 'Include Cycle Bill 2-12 Only';
    } else if (min === 2 && max === 5) {
      titleRange = '> 1'; // 'Include Cycle Bill > 1 Only';
      // eslint-disable-next-line no-dupe-else-if
    } else if (min === 3 && max === 4) {
      titleRange = '4-12'; // 'Include Cycle Bill 4-12 Only';
    } else if (min === 3 && max === 5) {
      titleRange = '> 3'; // 'Include Cycle Bill > 3 Only';
      // eslint-disable-next-line no-dupe-else-if
    } else if (min === 4 && max === 5) {
      titleRange = '> 6'; // 'Include Cycle Bill > 6 Only';
    }
    if (0 < min && min < 6 && 0 < max && max < 6 && titleRange !== '') {
      title = 'main.filters.include_cycle_bill_x_only';
    }
    if (xlsExport) {
      const value = this.formatService
        .translateAndFormat(title)
        .replace('${1}', titleRange);
      return value;
    } else {
      return [title, titleRange];
    }
  }
  // TODO: It looks like this endpoint is already being retrieved by the local-formats.service
  getLocaleFormats = (): Observable<Array<LocaleFormat>> => {
    return this.http
      .clone()
      .disableRoutingAbort()
      .get('/filter/locale-formats')
      .pipe(
        retryBeforeThrow({ label: 'FilterDataService.getLocaleFormats' }),
        map((res) => res as Array<LocaleFormat>),
        catchError((err) => {
          return this.handleError(
            err,
            'FilterDataService.getLocaleFormats Error'
          );
        })
      );
  };
}
